import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Scale, CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

const ConfirmAppointmentPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, already_confirmed
  const [message, setMessage] = useState('');
  const [appointmentData, setAppointmentData] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'already_used', 'invalid', 'generic'

  useEffect(() => {
    const confirmAppointment = async () => {
      try {
        const response = await api.get(`/appointments/confirm/${token}`);
        setStatus('success');
        setMessage(response.data.message);
        setAppointmentData(response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Errore nella conferma dell\'appuntamento';
        setStatus('error');
        setMessage(errorMessage);
        
        // Determine error type for better UX
        if (errorMessage.includes('già utilizzato') || errorMessage.includes('già confermato')) {
          setErrorType('already_used');
        } else if (errorMessage.includes('non valido') || errorMessage.includes('scaduto')) {
          setErrorType('invalid');
        } else {
          setErrorType('generic');
        }
      }
    };

    if (token) {
      confirmAppointment();
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary to-primary/90 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <Scale className="h-10 w-10 text-secondary" />
          <h1 className="text-2xl font-bold text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>
            Studio Legale Nada Zhiti
          </h1>
        </div>

        {status === 'loading' && (
          <div className="py-8">
            <Loader2 className="h-16 w-16 mx-auto mb-4 text-secondary animate-spin" />
            <p className="text-lg text-muted-foreground">Conferma in corso...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="py-8">
            <CheckCircle className="h-20 w-20 mx-auto mb-4 text-success" />
            <h2 className="text-2xl font-bold text-success mb-2">Appuntamento Confermato!</h2>
            <p className="text-muted-foreground mb-4">{message}</p>
            {appointmentData && (
              <div className="bg-success/10 p-4 rounded-lg mb-6">
                <p className="font-medium">Data: {appointmentData.appointment_date}</p>
                <p className="font-medium">Ora: {appointmentData.appointment_time}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground mb-6">
              Riceverai una email di conferma. Ti aspettiamo!
            </p>
            <Button onClick={() => navigate('/')} className="bg-secondary text-primary hover:bg-secondary/90">
              Torna alla Home
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="py-8" data-testid="error-state">
            {errorType === 'already_used' ? (
              <>
                <AlertTriangle className="h-20 w-20 mx-auto mb-4 text-amber-500" />
                <h2 className="text-2xl font-bold text-amber-600 mb-2">Link Già Utilizzato</h2>
                <p className="text-muted-foreground mb-4">{message}</p>
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-6 text-sm">
                  <p className="text-amber-800">
                    <strong>Buone notizie!</strong> L'appuntamento è già stato confermato in precedenza.
                    Non è necessaria alcuna ulteriore azione.
                  </p>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Per qualsiasi domanda, contattaci al <strong>3462393411</strong>
                </p>
                <Button onClick={() => navigate('/')} className="bg-secondary text-primary hover:bg-secondary/90">
                  Torna alla Home
                </Button>
              </>
            ) : (
              <>
                <XCircle className="h-20 w-20 mx-auto mb-4 text-destructive" />
                <h2 className="text-2xl font-bold text-destructive mb-2">Link Non Valido</h2>
                <p className="text-muted-foreground mb-4">{message}</p>
                <div className="bg-muted/50 p-4 rounded-lg mb-6 text-sm">
                  <p className="mb-2"><strong>Possibili motivi:</strong></p>
                  <ul className="text-left list-disc list-inside space-y-1">
                    <li>Il link è già stato utilizzato</li>
                    <li>Il link è scaduto</li>
                    <li>L'appuntamento è già stato confermato</li>
                  </ul>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Per assistenza contattaci al <strong>3462393411</strong>
                </p>
                <Button onClick={() => navigate('/')} variant="outline">
                  Torna alla Home
                </Button>
              </>
            )}
          </div>
        )}

        <div className="mt-6 pt-6 border-t text-sm text-muted-foreground">
          <p>Via Roma, 24 - Casale Monferrato</p>
          <p>Tel: 3462393411 | P.IVA: 02680260029</p>
        </div>
      </Card>
    </div>
  );
};

export default ConfirmAppointmentPage;
